import { ref } from 'vue'
import { hoursService } from '../../services/hoursService.js'
import { SWP_EMPLOYEE_ID } from '../../constants/swp.js'

/**
 * Composable for shared availability data loading and calculations.
 * Provides caching, match calculations, and styling helpers.
 */
export function useAvailability() {
  // Cache: { 'startDate-endDate': { data: [], timestamp: number } }
  const availabilityCache = ref({})
  const isLoading = ref(false)

  /**
   * Get weekly availability for a date range (with 5-minute caching)
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @param {boolean} forceRefresh - Force refresh ignoring cache
   * @param {string} availabilityMode - One of 'definitief', 'presourcing', 'voorgesteld'
   * @returns {Promise<Array>} - Array of employees with weeks data
   */
  async function getWeeklyAvailability(
    startDate,
    endDate,
    forceRefresh = false,
    availabilityMode = 'presourcing'
  ) {
    if (!startDate || !endDate) return []

    // Normalize dates to YYYY-MM-DD format
    const normalizedStart = startDate.split('T')[0]
    const normalizedEnd = endDate.split('T')[0]
    const mode = availabilityMode || 'presourcing'
    const cacheKey = `${normalizedStart}-${normalizedEnd}-${mode}`

    // Check cache (5 minute TTL)
    const cached = availabilityCache.value[cacheKey]
    const cacheValid = cached && Date.now() - cached.timestamp < 5 * 60 * 1000

    if (!forceRefresh && cacheValid) {
      return cached.data
    }

    isLoading.value = true
    try {
      const params = {
        start_date: normalizedStart,
        end_date: normalizedEnd
      }
      // Only include availability_mode when not the default (presourcing)
      if (mode && mode !== 'presourcing') {
        params.availability_mode = mode
      }
      const result = await hoursService.getWeeklyAvailability(params)

      const data = result?.data || []

      // Update cache
      availabilityCache.value[cacheKey] = {
        data,
        timestamp: Date.now()
      }

      return data
    } catch (e) {
      console.error('Error loading weekly availability:', e)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Calculate match percentage and average available days for an employee's weeks
   * @param {Array} employeeWeeks - Array of week objects with available_days
   * @param {number} requiredDpw - Required days per week
   * @param {string} employeeId
   * @returns {{ matchPercent: number, avgAvailableDays: string }}
   */
  function calculateMatchStats(employeeWeeks, requiredDpw, employeeId) {
    if (!employeeWeeks || employeeWeeks.length === 0 || !requiredDpw || requiredDpw <= 0) {
      return { matchPercent: 0, avgAvailableDays: '0' }
    }

    // SWP is a placeholder, not a real employee — always 0% match
    if (employeeId === SWP_EMPLOYEE_ID) {
      return { matchPercent: 0, avgAvailableDays: '0' }
    }

    let weeksCanFulfill = 0
    let totalAvailableDays = 0

    for (const week of employeeWeeks) {
      const availableDays = Number(week.available_days || 0)
      totalAvailableDays += availableDays
      if (availableDays >= requiredDpw) {
        weeksCanFulfill++
      }
    }

    const matchPercent = Math.round((weeksCanFulfill / employeeWeeks.length) * 100)
    const avgAvailableDays = (totalAvailableDays / employeeWeeks.length).toFixed(1)

    return { matchPercent, avgAvailableDays }
  }

  /**
   * Get match percentage text color class (Tailwind)
   * @param {number} matchPercent
   * @returns {string}
   */
  function getMatchColor(matchPercent) {
    if (matchPercent >= 80) return 'text-green-600 dark:text-green-400'
    if (matchPercent >= 50) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-red-600 dark:text-red-400'
  }

  /**
   * Get match percentage background color class (Tailwind)
   * @param {number} matchPercent
   * @returns {string}
   */
  function getMatchBgColor(matchPercent) {
    if (matchPercent >= 80) return 'bg-green-100 dark:bg-green-900/50'
    if (matchPercent >= 50) return 'bg-yellow-100 dark:bg-yellow-900/50'
    return 'bg-red-100 dark:bg-red-900/50'
  }

  /**
   * Get match badge class (combined bg + text)
   * @param {number} matchPercent
   * @returns {string}
   */
  function getMatchBadgeClass(matchPercent) {
    if (matchPercent >= 80)
      return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
    if (matchPercent >= 50)
      return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
    return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
  }

  /**
   * Clear cache for a specific date range
   * @param {string} startDate
   * @param {string} endDate
   */
  function clearCache(startDate, endDate) {
    if (startDate && endDate) {
      const prefix = `${startDate.split('T')[0]}-${endDate.split('T')[0]}`
      for (const key of Object.keys(availabilityCache.value)) {
        if (key.startsWith(prefix)) {
          delete availabilityCache.value[key]
        }
      }
    }
  }

  /**
   * Clear all cached data
   */
  function clearAllCache() {
    availabilityCache.value = {}
  }

  return {
    // State
    availabilityCache,
    isLoading,

    // Loading functions
    getWeeklyAvailability,

    // Calculation functions
    calculateMatchStats,

    // Styling helpers
    getMatchColor,
    getMatchBgColor,
    getMatchBadgeClass,

    // Cache management
    clearCache,
    clearAllCache
  }
}
